// Variation selector UX — luxury-grade redesign of WC's stock pill UI.
//
// Three problems this fixes
// -------------------------
// 1. WC renders ALL secondary-axis options (e.g. all 9 size pills) even
//    when the current primary selection (gender) only has variations for
//    some of them. Sizes 3-5 show when MEN is picked but no MEN+3/4/5
//    variation exists. → We HIDE pills that don't exist for the current
//    primary. Hidden, not strikethrough — the option just doesn't apply.
//
// 2. WC's "disabled" state uses a diagonal-strikethrough overlay that
//    users misread as broken / available-but-buggy. → Strikethrough is
//    suppressed entirely via CSS; nonexistent combos are hidden, OOS
//    combos surface an inline "Notify me" affordance instead.
//
// 3. Cascading-disable trap: pick MEN + size 9, then WOMEN gets greyed
//    because no WOMEN+9 variation exists, and the user can't switch.
//    → Primary axis (gender) is always clickable. On any primary click
//    we clear the secondary selection first, then let WC's normal click
//    flow assign the new primary. Layout re-renders for the new scale.
//
// Data source
// -----------
// includes/variation-map.php prints a small <script id="sentiments-
// variation-map"> tag in the footer with the map shape:
//   { primaryAttr, secondaryAttr, map: { primary_value: { secondary_value: 'in'|'out' } } }
//
// Stock status is included so we can surface an OOS notice for selected
// out-of-stock sizes without re-querying the server.

const MAP_KEY            = 'sentimentsVariationMap';
const FORM_SELECTOR      = 'form.wp-block-add-to-cart-with-options';
const ROOT_SELECTOR      = '.wp-block-woocommerce-add-to-cart-with-options-variation-selector';
const ATTR_SELECTOR      = '.wp-block-woocommerce-add-to-cart-with-options-variation-selector-attribute';
const PILL_SELECTOR      = 'label.wc-block-add-to-cart-with-options-variation-selector-attribute-options__pill';
const INPUT_SELECTOR     = '.wc-block-add-to-cart-with-options-variation-selector-attribute-options__pill-input';
const STOCK_INDICATOR    = '.wc-block-components-product-stock-indicator';

const getCheckedInput = (attr) => attr.querySelector(`${INPUT_SELECTOR}:checked`);

const getSelectedPrimary = (primaryAttr) => {
	const checked = getCheckedInput(primaryAttr);
	return checked ? checked.value : null;
};

// Resolve the lookup table for secondary pills given the variation map
// and the currently-selected primary value.
//   - 2-axis map (e.g. GENDER + SIZE(UK)): returns the slice for the
//     selected primary, or null when nothing is picked yet.
//   - 1-axis map (e.g. MEN SIZE(UK) on sneakers): the map IS the flat
//     lookup; primary is irrelevant and always returns the whole map.
const resolveValidSet = (data, primaryValue) => {
	if (!data.primaryAttr) return data.map; // 1-axis — flat map
	return primaryValue && data.map[primaryValue] ? data.map[primaryValue] : null;
};

// Walk all pills in the secondary attribute and set data-stock based on
// the lookup. For 2-axis maps, also set data-not-available on pills whose
// value doesn't exist under the current primary (CSS hides them).
const applyFilter = (secondaryAttr, data, primaryValue) => {
	const validSet = resolveValidSet(data, primaryValue);

	secondaryAttr.querySelectorAll(PILL_SELECTOR).forEach((pill) => {
		const input = pill.querySelector(INPUT_SELECTOR);
		if (!input) return;
		const sval = input.value;

		if (!validSet) {
			// 2-axis with no primary picked — show all, no stock label.
			delete pill.dataset.notAvailable;
			delete pill.dataset.stock;
			return;
		}

		if (Object.prototype.hasOwnProperty.call(validSet, sval)) {
			delete pill.dataset.notAvailable;
			pill.dataset.stock = validSet[sval]; // 'in' or 'out'
			// Force-enable in case WC has it marked disabled (cascading
			// disable trap — our filter is now authoritative).
			if (input.disabled) input.disabled = false;
		} else {
			// Nonexistent for this primary — hide entirely (2-axis only;
			// 1-axis maps always contain every pill so this never fires).
			pill.dataset.notAvailable = 'true';
			delete pill.dataset.stock;
			if (input.checked) {
				input.checked = false;
				input.dispatchEvent(new Event('change', { bubbles: true }));
			}
		}
	});
};

// Show/hide the "Notify me when available →" link based on whether the
// currently-checked secondary pill is out of stock. The link sits as a
// sibling of the native WC stock indicator so the two read as one line
// ("Out of stock  Notify me when available →"). Also flags the form
// with `is-out-of-stock` so CSS can hide Add-to-cart + Buy-now.
const updateOOSState = (secondaryAttr, notifyLink, form) => {
	const checked = getCheckedInput(secondaryAttr);
	let isOOS = false;
	if (checked) {
		const pill = checked.closest(PILL_SELECTOR);
		isOOS = !!(pill && pill.dataset.stock === 'out');
	}
	if (notifyLink) notifyLink.hidden = !isOOS;
	// Class lives on <body> (not on the form) because the Buy-now button
	// renders OUTSIDE the WC form, in a sibling block-group container.
	// A body-level class is the simplest selector ancestor for both.
	document.body.classList.toggle('is-variation-oos', isOOS);
	// Keep the form class too so any form-local styling that wants it has
	// access (no current consumer, but cheap to maintain).
	if (form) form.classList.toggle('is-out-of-stock', isOOS);
};

export function initVariationUX() {
	const data = window[MAP_KEY];
	if (!data || !data.map) return;

	const root = document.querySelector(ROOT_SELECTOR);
	if (!root) return;

	const form = root.closest(FORM_SELECTOR);

	const attrs = root.querySelectorAll(ATTR_SELECTOR);
	if (!attrs.length) return; // no variation pills at all

	// 1-axis (single attribute, e.g. MEN SIZE(UK) on sneakers): primary is
	// null, secondary is the only axis. 2-axis: primary = attrs[0],
	// secondary = attrs[1]. The PHP map shape matches accordingly.
	const isSingleAxis  = !data.primaryAttr;
	const primaryAttr   = isSingleAxis ? null : attrs[0];
	const secondaryAttr = isSingleAxis ? attrs[0] : attrs[1];

	const readPrimary = () => (primaryAttr ? getSelectedPrimary(primaryAttr) : null);

	// Append the "Notify me" link to the native WC stock indicator so the
	// existing "Out of stock" text and our affordance render inline.
	let notifyLink = null;
	const stockIndicator = document.querySelector(STOCK_INDICATOR);
	if (stockIndicator) {
		notifyLink = document.createElement('a');
		notifyLink.href = '#';
		notifyLink.className = 'sentiments-notify-link';
		notifyLink.dataset.sentimentsNotify = '';
		notifyLink.textContent = 'Notify me when available →';
		notifyLink.hidden = true;
		stockIndicator.insertAdjacentElement('afterend', notifyLink);
	}

	// Free-switch the primary axis (e.g. GENDER). Skipped on 1-axis
	// products where there is no primary to switch. Capture phase so we
	// run BEFORE WC's Interactivity click handler — clear the secondary
	// selection and force-enable the clicked primary input.
	if (primaryAttr) {
		primaryAttr.addEventListener('click', (e) => {
			const pill = e.target.closest(PILL_SELECTOR);
			if (!pill) return;
			const input = pill.querySelector(INPUT_SELECTOR);
			if (!input || input.checked) return;

			// Force-enable so WC's click can actually fire (browser default
			// suppresses click on a disabled input).
			if (input.disabled) input.disabled = false;

			// Clear every checked secondary so the new primary isn't blocked
			// by a now-invalid combo.
			secondaryAttr.querySelectorAll(`${INPUT_SELECTOR}:checked`).forEach((other) => {
				other.checked = false;
				other.dispatchEvent(new Event('change', { bubbles: true }));
			});

			// WC's handler proceeds next; apply our filter after its state settles.
			setTimeout(() => {
				applyFilter(secondaryAttr, data, readPrimary());
				updateOOSState(secondaryAttr, notifyLink, form);
			}, 0);
		}, true);
	}

	// Toggle the notify link when the size changes.
	secondaryAttr.addEventListener('click', () => {
		setTimeout(() => updateOOSState(secondaryAttr, notifyLink, form), 0);
	});

	// Initial pass — apply filter for whatever primary is selected at boot
	// (no-op on 1-axis products since the map is flat).
	applyFilter(secondaryAttr, data, readPrimary());
	updateOOSState(secondaryAttr, notifyLink, form);

	// Safety net: if WC re-renders the pills (state changes via the
	// Interactivity API), our data-not-available attributes might be
	// wiped. Watch for attribute mutations on inputs and re-filter.
	const mo = new MutationObserver(() => {
		applyFilter(secondaryAttr, data, readPrimary());
		updateOOSState(secondaryAttr, notifyLink, form);
	});
	mo.observe(root, {
		subtree: true,
		attributes: true,
		attributeFilter: ['checked', 'disabled'],
	});
}
